import { Keyboard } from 'lucide-react'
import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from 'react'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '#app/components/ui/tooltip'
import { useKeyboardShortcuts } from '#app/utils/hooks/useKeyboardShortcuts'
import { cn } from '#app/utils/misc.tsx'

function ShortcutsTooltip() {
	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>
					<button
						type="button"
						className="absolute right-2 top-2 text-muted-foreground/50 hover:text-muted-foreground"
					>
						<Keyboard size={16} />
					</button>
				</TooltipTrigger>
				<TooltipContent align="end">
					<p className="font-sans text-xs">
						<kbd className="rounded-md bg-muted px-1 py-0.5 text-muted-foreground">
							⌘
						</kbd>
						+
						<kbd className="rounded-md bg-muted px-1 py-0.5 text-muted-foreground">
							b
						</kbd>{' '}
						for bold
					</p>
					<p className="font-sans text-xs">
						{' '}
						<kbd className="rounded-md bg-muted px-1 py-0.5 text-muted-foreground">
							⌘
						</kbd>
						+
						<kbd className="rounded-md bg-muted px-1 py-0.5 text-muted-foreground">
							i
						</kbd>{' '}
						for italic
					</p>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	)
}

export interface TextareaProps
	extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
	enableFormatting?: boolean
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
	(
		{ className, onChange, value, enableFormatting = false, ...props },
		forwardedRef,
	) => {
		const localRef = useRef<HTMLTextAreaElement>(null)
		const [isFocused, setIsFocused] = useState(false)
		const selectionRefForControlledUsage = useRef<{
			start: number | null
			end: number | null
		}>({ start: null, end: null })

		useEffect(() => {
			if (selectionRefForControlledUsage.current.start !== null) {
				const textarea = localRef?.current
				if (textarea) {
					textarea.selectionStart = selectionRefForControlledUsage.current.start
					textarea.selectionEnd = selectionRefForControlledUsage.current.end!
					selectionRefForControlledUsage.current = { start: null, end: null }
				}
			}
		}, [value])

		// TODO: Make this more sophisticated by handling undo, etc.
		useKeyboardShortcuts(
			{
				b: () => {
					handleAddFormatting('**')
				},
				i: () => {
					handleAddFormatting('*')
				},
			},
			isFocused,
		)

		// Make the local ref available to the parent component
		useImperativeHandle(forwardedRef, () => localRef.current!, [])

		const handleAddFormatting = (formatting: string) => {
			if (!enableFormatting) {
				return
			}
			const textarea = localRef?.current
			if (!textarea) {
				console.error('textarea not found')
				return
			}

			const start = textarea.selectionStart
			const end = textarea.selectionEnd
			const text = textarea.value

			const newText =
				text.substring(0, start) +
				formatting +
				text.substring(start, end) +
				formatting +
				text.substring(end)

			// Support usage as a controlled or uncontrolled component!
			if (onChange) {
				// If using as a controlled component, we need to set the selection after the onChange event
				// propagates and causes a re-render.
				selectionRefForControlledUsage.current = {
					start: start + formatting.length,
					end: end + formatting.length,
				}
				onChange({
					target: {
						value: newText,
					},
				} as React.ChangeEvent<HTMLTextAreaElement>)
			} else {
				textarea.value = newText
				textarea.selectionStart = start + formatting.length
				textarea.selectionEnd = end + formatting.length

				// Dispatch an event to trigger any handlers that may be listening for input events (e.g. Conform)
				textarea.dispatchEvent(new Event('input', { bubbles: true }))
			}
		}

		return (
			<div className="relative w-full">
				<textarea
					className={cn(
						'flex min-h-[80px] w-full rounded-md border border-input bg-background py-2 pl-2.5 pr-6 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid]:border-input-invalid md:text-sm',
						className,
					)}
					ref={localRef}
					onChange={onChange}
					value={value}
					onFocus={() => setIsFocused(true)}
					onBlur={() => setIsFocused(false)}
					{...props}
				/>
				{enableFormatting && <ShortcutsTooltip />}
			</div>
		)
	},
)
Textarea.displayName = 'Textarea'

export { Textarea }
